import React, { useEffect, useState } from 'react'
import {
  Typography,
  Box,
  TextField,
  InputLabel,
  ToggleButtonGroup,
  ToggleButton,
  Button,
  Snackbar,
  InputAdornment
} from '@mui/material'
import { GppGoodOutlined, ShieldOutlined } from '@mui/icons-material'
import { FormProvider, useForm, Controller } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { useQuery, useMutation } from '@tanstack/react-query'
import { useParams, useNavigate } from 'react-router-dom'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import { fetchCampaign, createFlight, updateFlight } from '../getData'
import { pacificDateStringToUtcStartOfDay, pacificDateStringToUtcEndOfDay } from '../utils'
import TwoColumnLayout from '../TwoColumnLayout'
import FlightSidebar from '../FlightSidebar'
import FlightDates from '../FlightDates'
import InputErrorMessage from '../InputErrorMessage'
import CircularLoader from '../CircularLoader'
import theme from '../theme'
import {
  FlightInput,
  FlightInputSchema,
  FlightImpressions,
  FlightFrequency,
  ErrorResponse
} from '../../types/campaign'
import NewFlightIcon from '../icons/NewFlight'
import { AxiosError } from 'axios'
import { PACIFIC_TIMEZONE } from '../../types/constants'

dayjs.extend(utc)
dayjs.extend(timezone)

interface Props {
  defaultValues?: Partial<FlightInput>
}

const CreateFlight = ({ defaultValues }: Props) => {
  const { id: campaignId, flightId: flightIdParam } = useParams()
  const navigate = useNavigate()

  const { data: campaign } = useQuery({
    queryKey: ['campaign', campaignId],
    queryFn: () => fetchCampaign(Number(campaignId))
  })

  const [flightId, setFlightId] = useState(
    flightIdParam ? Number(flightIdParam) : undefined
  )
  const [openSnackbar, setOpenSnackbar] = useState({ open: false, message: '' })
  const [budgetInputValue, setBudgetInputValue] = useState('')
  const [dateTouched, setDateTouched] = useState<{ startTouched: boolean; endTouched: boolean }>({ startTouched: false, endTouched: false })
  const [initialDuration, setInitialDuration] = useState<{ starts_at: string | null; ends_at: string | null }>({ starts_at: null, ends_at: null })

  const formMethods = useForm<FlightInput>({
    mode: 'all',
    resolver: yupResolver(FlightInputSchema),
    defaultValues: {
      name: '',
      description: '',
      duration: {
        starts_at: '',
        ends_at: ''
      },
      is_guaranteed: true,
      delivery: {
        type: campaign?.delivery_type,
        impression_target: 1,
        play_frequency_seconds: 15,
        budget: 0,
        cpm: 0
      },
      ...defaultValues
    }
  })

  useEffect(() => {
    if (campaign) {
      formMethods.setValue('delivery.type', campaign.delivery_type)

      const currentFlight = campaign.flights.find(
        (flight) => flight.id === Number(flightIdParam)
      )

      if (currentFlight) {
        setInitialDuration({
          starts_at: currentFlight.duration.starts_at,
          ends_at: currentFlight.duration.ends_at
        })
        
        formMethods.reset({
          name: currentFlight.name ?? '',
          description: currentFlight.description ?? '',
          duration: {
            starts_at:
              dayjs.utc(currentFlight.duration.starts_at)
                .tz(PACIFIC_TIMEZONE)
                .format('YYYY-MM-DD HH:mm:ss') ?? '',
            ends_at:
              dayjs.utc(currentFlight.duration.ends_at)
                .tz(PACIFIC_TIMEZONE)
                .format('YYYY-MM-DD HH:mm:ss') ?? ''
          },
          is_guaranteed: currentFlight.is_guaranteed,
          delivery: {
            type: currentFlight.delivery.type,
            budget: Number(currentFlight.delivery.budget) || 0,
            impression_target:
              (currentFlight.delivery as FlightImpressions)
                ?.impression_target ?? 1,
            play_frequency_seconds:
              (currentFlight.delivery as FlightFrequency)
                ?.play_frequency_seconds ?? 15,
            cpm: (currentFlight.delivery as FlightImpressions)?.cpm ?? 0
          }
        })
        // Initialize budget input value with formatting
        const budgetValue = Math.round(Number(currentFlight.delivery.budget)) || 0
        const formattedBudget = budgetValue.toLocaleString('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        })
        setBudgetInputValue(formattedBudget)
      } else {
        setInitialDuration({ starts_at: null, ends_at: null })
      }
      setDateTouched({ startTouched: false, endTouched: false })
    }
  }, [campaign, flightIdParam])

  const {
    formState: { errors },
    handleSubmit
  } = formMethods
  const formValues = formMethods.getValues()
  const flightEnded = formValues.duration.ends_at
    ? dayjs.tz(formValues.duration.ends_at, 'YYYY-MM-DD HH:mm:ss', PACIFIC_TIMEZONE).isBefore(dayjs().tz(PACIFIC_TIMEZONE))
    : false

  const { mutate: createFlightMutation, isPending: isCreatingFlight } =
    useMutation({
      mutationFn: (flight: FlightInput) =>
        createFlight(flight, Number(campaignId)),
      onSuccess: (data) => {
        setOpenSnackbar({ open: true, message: 'Flight created successfully' })
        setFlightId(data.id)
        navigate(`/campaign/${campaignId}/flight/${data.id}/targets`, {
          state: { campaign }
        })
      },
      onError: (error: AxiosError) => {
        console.error(error)
        const response = error.response?.data as ErrorResponse
        setOpenSnackbar({
          open: true,
          message: response.context?.[0] || 'Error creating flight'
        })
      }
    })

  const { mutate: updateFlightMutation, isPending: isUpdatingFlight } =
    useMutation({
      mutationFn: (flight: FlightInput) => updateFlight(flight, flightId),
      onSuccess: () => {
        setOpenSnackbar({
          open: true,
          message: 'Flight updated successfully'
        })
        if (!flightEnded) {
          navigate(`/campaign/${campaignId}/flight/${flightId}/targets`)
        } else {
          navigate(`/campaign/${campaignId}`)
        }
      },
      onError: (error: AxiosError) => {
        console.error(error)
        const response = error.response?.data as ErrorResponse
        setOpenSnackbar({
          open: true,
          message: response.context?.[0] || 'Error updating flight'
        })
      }
    })

  const handleSaveFlight = handleSubmit(() => {
    const formValues = formMethods.getValues()

    const { starts_at, ends_at } = formValues.duration
  
    const startsAtUtc = dateTouched.startTouched && starts_at
      ? pacificDateStringToUtcStartOfDay(starts_at)
      : (initialDuration.starts_at ?? (starts_at ? pacificDateStringToUtcStartOfDay(starts_at) : ''))
    
    const endsAtUtc = dateTouched.endTouched && ends_at
      ? pacificDateStringToUtcEndOfDay(ends_at)
      : (initialDuration.ends_at ?? (ends_at ? pacificDateStringToUtcEndOfDay(ends_at) : ''))
    
    const inputData = {
      ...formValues,
      duration: {
        starts_at: startsAtUtc,
        ends_at: endsAtUtc
      }
    }
    if (flightId) {
      updateFlightMutation(inputData)
    } else {
      createFlightMutation(inputData)
    }
  })

  const header = (
    <Typography
      variant="heading-8"
      sx={{
        width: '65%',
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        paddingBottom: '20px'
      }}
    >
      <NewFlightIcon
        fontSize="small"
        sx={{
          color: theme.palette['base-black'],
          marginRight: theme.space['20'],
          height: '28px',
          width: '28px'
        }}
      />
      {flightId ? 'Edit Flight' : 'New Flight'}
    </Typography>
  )

  const footer = (
    <Box
      sx={{
        marginTop: theme.space['40'],
        display: 'flex',
        justifyContent: 'center',
        gap: theme.space['20']
      }}
    >
      <Button
        variant="contained"
        color="secondary"
        sx={{ width: '300px', height: '56px' }}
        onClick={() => navigate(`/campaign/${campaignId}`)}
      >
        <Typography variant="ui-3">Cancel</Typography>
      </Button>
      <Button
        data-testid="flight-save-button"
        color="primary"
        variant="contained"
        sx={{ width: '300px', height: '56px' }}
        onClick={() => handleSaveFlight()}
        disabled={isCreatingFlight || isUpdatingFlight}
      >
        <Typography variant="ui-3">
          {flightEnded ? 'Save' : 'Save & Continue'}
        </Typography>
      </Button>
    </Box>
  )

  const getInputLabel = (
    field: string,
    text: string,
    bottomMargin?: string
  ) => {
    return (
      <InputLabel
        id={`${field}-label`}
        sx={{
          marginBottom: bottomMargin ?? theme.space['8'],
          color:
            field in errors
              ? theme.palette['red-500']
              : theme.palette['sage-900']
        }}
      >
        <Typography variant="body-11" sx={{ fontWeight: 500 }}>
          {text}
        </Typography>
      </InputLabel>
    )
  }

  const flightDetails = (
    <Box>
      {header}
      <FormProvider {...formMethods}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          <Box
            sx={{
              borderRadius: theme.borders['rounded-16'],
              padding: theme.space['40'],
              backgroundColor: theme.palette['base-white'],
              display: 'flex',
              flexDirection: 'column',
              gap: theme.space['32']
            }}
          >
            <Typography variant="heading-4">Details</Typography>
            <Controller
              name="name"
              control={formMethods.control}
              rules={{
                required: 'This field is required'
              }}
              render={({ field }) => (
                <Box>
                  {getInputLabel('name', 'Flight Name')}
                  <TextField
                    {...field}
                    fullWidth
                    margin="dense"
                    error={!!errors.name}
                  />
                  {errors.name && (
                    <InputErrorMessage error={errors.name.message} />
                  )}
                </Box>
              )}
            />
            <Controller
              name="description"
              control={formMethods.control}
              rules={{
                required: 'This field is required'
              }}
              render={({ field }) => (
                <Box>
                  {getInputLabel('description', 'Description (Optional)')}
                  <TextField
                    {...field}
                    fullWidth
                    margin="dense"
                    error={!!errors.description}
                  />
                  {errors.description && (
                    <InputErrorMessage error={errors.description.message} />
                  )}
                </Box>
              )}
            />
          </Box>
          <Box
            sx={{
              borderRadius: theme.borders['rounded-16'],
              padding: theme.space['40'],
              backgroundColor: theme.palette['base-white'],
              display: 'flex',
              flexDirection: 'row',
              gap: theme.space['32'],
              flexWrap: 'wrap'
            }}
          >
            <Typography variant="heading-4" sx={{ width: '100%' }}>
              Spend
            </Typography>
            <Controller
              name="delivery.budget"
              control={formMethods.control}
              render={({ field }) => (
                <Box sx={{ width: 'calc(50% - 16px)' }}>
                  {getInputLabel('delivery.budget', 'Budget')}
                  <TextField
                    fullWidth
                    margin="dense"
                    type="text"
                    disabled={flightEnded}
                    value={budgetInputValue || (field.value ? field.value.toString() : '')}
                    onChange={(e) => {
                      const cleanValue = e.target.value.replace(/[^0-9]/g, '')
                      setBudgetInputValue(cleanValue)
                      const parsedValue = parseInt(cleanValue) || 0
                      field.onChange(parsedValue)
                    }}
                    onBlur={(e) => {
                      field.onBlur()
                      // Format the value on blur with commas and 2 decimal places
                      const cleanValue = e.target.value.replace(/[^0-9]/g, '')
                      const parsedValue = parseInt(cleanValue) || 0
                      const formattedValue = parsedValue.toLocaleString('en-US', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2
                      })
                      setBudgetInputValue(formattedValue)
                    }}
                    onFocus={() => {
                      // Show raw value when focused for easier editing
                      setBudgetInputValue(field.value ? field.value.toString() : '')
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment
                          position="start"
                          sx={{
                            margin: 0,
                            padding: 0,
                            height: '100%',
                            '& .MuiInputAdornment-root': {
                              margin: 0,
                              padding: 0
                            }
                          }}
                        >
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              backgroundColor: theme.palette['sage-100'],
                              color: theme.palette['sage-500'],
                              borderRight: `1px solid ${theme.palette['sage-200']}`,
                              width: '32px',
                              height: '57px',
                              marginLeft: '-14px',
                              marginTop: '-1px',
                              marginBottom: '-1px',
                              marginRight: theme.space['8'],
                              borderRadius: '6px 0 0 6px'
                            }}
                          >
                            <Typography variant="body-10">
                              $
                            </Typography>
                          </Box>
                        </InputAdornment>
                      )
                    }}
                    inputProps={{
                      placeholder: '0.00',
                      style: { textAlign: 'left' }
                    }}
                  />
                  {errors.delivery?.budget && (
                    <InputErrorMessage error={errors.delivery.budget.message} />
                  )}
                </Box>
              )}
            />
            {campaign?.delivery_type === 'impressions' && (
              <Controller
                name="delivery.impression_target"
                control={formMethods.control}
                render={({ field }) => (
                  <Box sx={{ width: 'calc(50% - 16px)' }}>
                    {getInputLabel('delivery.impression_target', 'Impressions')}
                    <TextField
                      {...field}
                      value={field.value ?? ''}
                      fullWidth
                      margin="dense"
                      type="number"
                      inputProps={{
                        min: 0
                      }}
                      onChange={(e) => {
                        const value = parseInt(e.target.value)
                        field.onChange(value)
                      }}
                      disabled={flightEnded}
                    />
                    {errors.delivery?.impression_target && (
                      <InputErrorMessage
                        error={errors.delivery.impression_target.message}
                      />
                    )}
                  </Box>
                )}
              />
            )}
            {campaign?.delivery_type === 'play_frequency' && (
              <Controller
                name="delivery.play_frequency_seconds"
                control={formMethods.control}
                render={({ field }) => (
                  <Box sx={{ width: 'calc(50% - 16px)' }}>
                    {getInputLabel(
                      'delivery.play_frequency_seconds',
                      'Play Frequency'
                    )}
                    <TextField
                      {...field}
                      value={field.value ?? ''}
                      fullWidth
                      margin="dense"
                      type="number"
                      disabled={flightEnded}
                      onChange={(e) => {
                        const value = parseInt(e.target.value)
                        field.onChange(value)
                      }}
                    />
                    {errors.delivery?.play_frequency_seconds && (
                      <InputErrorMessage
                        error={errors.delivery.play_frequency_seconds.message}
                      />
                    )}
                  </Box>
                )}
              />
            )}
            <Box sx={{ width: '100%' }}>
              <Controller
                name="is_guaranteed"
                control={formMethods.control}
                rules={{
                  required: 'This field is required'
                }}
                render={({ field }) => (
                  <Box>
                    {getInputLabel(
                      'is_guaranteed',
                      'Is Guaranteed',
                      theme.space['12']
                    )}
                    <ToggleButtonGroup
                      {...field}
                      value={field.value}
                      onChange={(event, value) => field.onChange(value)}
                      exclusive
                      disabled={flightEnded}
                      sx={{
                        width: '100%',
                        '& .MuiButtonBase-root': {
                          border: '1px solid',
                          borderColor: theme.palette['sage-200'],
                          borderRadius: theme.borders['rounded-10'],
                          height: '56px'
                        },
                        '& .Mui-selected.Mui-disabled': {
                          backgroundColor: theme.palette['sage-200'],
                          borderColor: theme.palette['sage-200']
                        }
                      }}
                    >
                      <ToggleButton
                        value={true}
                        sx={{
                          width: '50%',
                          '&.Mui-selected': {
                            backgroundColor: theme.palette['sage-900'],
                            color: theme.palette['base-white']
                          }
                        }}
                      >
                        <GppGoodOutlined
                          fontSize="inherit"
                          sx={{ marginRight: theme.space['8'] }}
                        />
                        <Typography variant="body-8">Guaranteed</Typography>
                      </ToggleButton>
                      <ToggleButton
                        value={false}
                        sx={{
                          width: '50%',
                          '&.Mui-selected': {
                            backgroundColor: theme.palette['sage-900'],
                            color: theme.palette['base-white']
                          }
                        }}
                      >
                        <ShieldOutlined
                          fontSize="inherit"
                          sx={{ marginRight: theme.space['8'] }}
                        />
                        <Typography variant="body-8">Non-guaranteed</Typography>
                      </ToggleButton>
                    </ToggleButtonGroup>
                    {errors.is_guaranteed && (
                      <InputErrorMessage error={errors.is_guaranteed.message} />
                    )}
                  </Box>
                )}
              />
            </Box>
          </Box>
          <FlightDates 
            hasEnded={flightEnded}
            onDateTouchedChange={(flags) => setDateTouched((prev) => ({ ...prev, ...flags }))}
          />
        </Box>
      </FormProvider>
      {footer}
    </Box>
  )

  if (!campaign) {
    return <CircularLoader />
  }

  return (
    <Box>
      {isCreatingFlight || (isUpdatingFlight && <CircularLoader />)}
      <Snackbar
        open={openSnackbar.open}
        autoHideDuration={5000}
        onClose={() => setOpenSnackbar({ open: false, message: '' })}
        message={openSnackbar.message}
      />
      <TwoColumnLayout
        barPosition="right"
        sidebar={<FlightSidebar campaign={campaign} />}
        content={flightDetails}
      />
    </Box>
  )
}

export default CreateFlight
